"use client";
import { useState, useId } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import dynamic from "next/dynamic";

import InfoSvg from "assets/svg/info.svg";

import { demographicGraphicColors, socioeconomicStatusModalContent } from "mock/endeksa";

import SwiperWrapper from "components/swiper-wrapper";
import ModalTextContent from "components/modal-text-content";

import styles from "./styles.module.css";

const Modal = dynamic(() => import("components/common/modal"), { ssr: false });

ChartJS.register(ArcElement, Tooltip, Legend);

const DemographicGraphic = ({ districtReportDemographicInfos }) => {
  const keyId = useId();
  const { graphics } = districtReportDemographicInfos || {};
  const [modalOpen, setModalOpen] = useState(false);

  // to transform the information on the graph as desired.
  const addPercentageToLegends = (item, data) => {
    item.text = data?.datasets[0].data[item.index] ? `${item.text}: %${data?.datasets[0].data[item.index]}` : item.text;
    return item;
  };

  // WHY? Color codes and some ui styles are not coming by BE response, we had to add them later
  const setOtherPropertiesToGraphicData = (graphics) => {
    if (!graphics) return [];
    return graphics.map((graphic) => {
      const graphicKey = graphic.key || null;
      if (graphicKey && graphic) {
        graphic.datasets[0].backgroundColor = demographicGraphicColors[graphicKey];
        graphic.datasets[0].borderWidth = 2;
        graphic.datasets[0].hoverBorderWidth = 4;
      }
      return graphic;
    });
  };

  const circleOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        backgroundColor: "#fcfcfc",
        titleColor: "#2d393e",
        bodyColor: "#8f9fa9",
        borderWidth: 1,
        borderColor: "#bcc2c6",
        displayColors: false,
        callbacks: {
          label: (labels) => labels.formattedValue && `${labels.label}: %${labels.formattedValue}`,
        },
      },
      legend: {
        position: "right",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          font: {
            size: 12,
          },
          filter: addPercentageToLegends,
        },
        align: "center",
      },
    },
    animations: {
      animateScale: true,
    },
  };

  const graphicWithColors = setOtherPropertiesToGraphicData(graphics);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <div className={styles.demographicWrapper}>
      <SwiperWrapper>
        <Swiper navigation={true} loop={false} modules={[Navigation]} slidesPerView={2.04}>
          {graphicWithColors?.map((item, index) => {
            if (item?.labels) {
              return (
                <SwiperSlide key={`${keyId}+${index}`}>
                  <div className={styles.doughnutChartWrapper}>
                    <div className={styles.doughnutChartTitle}>
                      <p>{item.title}</p>
                      {item.title === "Sosyo Ekonomik Statü" && (
                        <div onClick={() => setModalOpen(true)} className={styles.infoIcon}>
                          <InfoSvg width="19" height="19" />
                        </div>
                      )}
                    </div>
                    <div className={styles.doughnutWrapper}>
                      <Doughnut height="190px" data={item} options={circleOptions} />
                    </div>
                  </div>
                </SwiperSlide>
              );
            }
            return null;
          })}
        </Swiper>
      </SwiperWrapper>
      <Modal isOpen={modalOpen} handleClose={handleCloseModal}>
        <ModalTextContent>
          <div className={styles.socioEconomicModalContent}>
            <p className={styles.modalTitle}>
              <b>{socioeconomicStatusModalContent.title}</b>
            </p>
            <p>{socioeconomicStatusModalContent.description}</p>
            {socioeconomicStatusModalContent.articles.map((article, index) => {
              return (
                <p key={index}>
                  <b>{article.articleTitle}</b>
                  {article.articleContent}
                </p>
              );
            })}
            <p
              className={styles.modalBottomDescription}
              dangerouslySetInnerHTML={{ __html: socioeconomicStatusModalContent.articleInfo }}
            />
          </div>
        </ModalTextContent>
      </Modal>
    </div>
  );
};

DemographicGraphic.propTypes = {
  districtReportDemographicInfos: PropTypes.object,
};

export default DemographicGraphic;
